import React, { useState, useEffect, useRef } from "react";
import InputField from "../Components/InputField";
import Button from "../Components/Button";

export interface EnvVar {
  key: string;
  value: string;
}

interface EnvVarPopupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (envVars: EnvVar[]) => void;
  initialEnvVars: EnvVar[];
}

const EnvVarPopupDialog: React.FC<EnvVarPopupDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialEnvVars,
}) => {
  const [newEnvVars, setNewEnvVars] = useState<EnvVar[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (isOpen) {
      setNewEnvVars([...initialEnvVars]);
    }
  }, [isOpen, initialEnvVars]);

  const handleAddRow = () => {
    setNewEnvVars([...newEnvVars, { key: "", value: "" }]);
  };

  const handleRemoveRow = (index: number) => {
    setNewEnvVars((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setNewEnvVars([]);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        const parsedVars = parseEnvFile(content);
        setNewEnvVars(parsedVars);
      }
    };
    reader.readAsText(file);
    
    // Reset file input so the same file can be uploaded again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const parseEnvFile = (fileContent: string): EnvVar[] => {
    const lines = fileContent.split('\n').filter(line => line.trim() !== '');
    
    return lines.map(line => {
      const [key, ...valueParts] = line.split('=');
      const value = valueParts.join('='); // Rejoin in case value contains = character
      
      return {
        key: key.trim(),
        value: value.trim()
      };
    });
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  return isOpen ? (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-lg">
        <h3 className="text-xl font-semibold mb-4 text-center">Environment Variables</h3>
        
        {/* File upload section */}
        <div className="mb-4">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".txt"
            className="hidden"
          />
          <Button
            onClick={triggerFileUpload}
            className="bg-black text-white px-5 w-full"
          >
            Upload Env File
          </Button>
          <p className="text-sm text-gray-500 mt-1">
            Format: key1=value1 (one per line)
          </p>
        </div>
        
        <div className="max-h-96 overflow-y-auto space-y-4">
          {newEnvVars.map((envVar, index) => (
            <div key={index} className="flex items-center space-x-2">
              <InputField
                name={`key-${index}`}
                value={envVar.key}
                onChange={(e) =>
                  setNewEnvVars((prev) =>
                    prev.map((v, i) =>
                      i === index ? { ...v, key: e.target.value } : v
                    )
                  )
                }
                placeholder="Key"
                className="w-full h-11 ml-2"
              />
              <InputField
                name={`value-${index}`}
                value={envVar.value}
                onChange={(e) =>
                  setNewEnvVars((prev) =>
                    prev.map((v, i) =>
                      i === index ? { ...v, value: e.target.value } : v
                    )
                  )
                }
                placeholder="Value"
                className="w-full h-11"
              />
              <Button
                onClick={() => handleRemoveRow(index)}
                className="bg-black text-white hover:bg-gray-800 px-5"
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
        
        <div className="mt-4 flex justify-between items-center">
          <div className="flex space-x-2">
            <Button
              onClick={handleAddRow}
              className="bg-black text-white hover:bg-gray-800 px-5"
            >
              + Add Row
            </Button>
            {newEnvVars.length > 0 && (
              <Button
                onClick={handleClearAll}
                className="bg-red-500 text-white hover:bg-red-600 px-5"
              >
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Footer section with thin line */}
        <div className="border-t border-gray-300 mt-4 pt-4">
          <div className="flex justify-end space-x-2">
            <Button
              onClick={onClose}
              className="border border-black text-black hover:bg-black hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={() => onConfirm(newEnvVars)}
              className="bg-black text-white hover:bg-gray-800"
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default EnvVarPopupDialog;